"use client";
import { useAtom, useSetAtom } from "jotai";
import {
  usePathname,
  useSearchParams,
  // eslint-disable-next-line no-restricted-imports
  useRouter as useNextRouter,
} from "next/navigation";
import BaseToggleSwitch from "./BaseToggleSwitch";
import { storeLangCookie } from "@/utils/functions/cookieOperations";
import { showSideMenuAtom } from "@/hooks/showSideMenuAtom";
import { progressAtom } from "@/hooks/headerHooks";
import { useDefaultLocale } from "@/providers/langProvider";

type TLang = "ar" | "en";

const LanguageSwitch = ({ mobile = false }) => {
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const router = useNextRouter();
  const defaultLocale = useDefaultLocale();
  const [, setShowSideMenu] = useAtom(showSideMenuAtom);
  const setProgress = useSetAtom(progressAtom);

  // Current locale straight from the URL — the store default is served unprefixed.
  // Reactive via usePathname, so the toggle always reflects the address bar.
  const seg = pathname.split("/")[1];
  const currentLocale: TLang =
    seg === "ar" || seg === "en" ? seg : defaultLocale;

  const goTo = (target: TLang) => {
    if (target === currentLocale) return;
    storeLangCookie(target);

    // Strip any existing locale prefix, then re-add the target prefix — UNLESS the
    // target is the store default (default locale is served unprefixed).
    const segs = pathname.split("/");
    if (segs[1] === "ar" || segs[1] === "en") segs.splice(1, 1);
    const bare = segs.join("/") || "/";
    const path =
      target === defaultLocale
        ? bare
        : `/${target}${bare === "/" ? "" : bare}`;

    const qs = searchParams.toString();
    if (mobile) setShowSideMenu(false);
    // Show the top loading bar for the switch — a locale change is a full route
    // navigation. RouterConfig clears progressAtom when the new route commits
    // (pathname change). computeRouterChange strips the locale, so the custom
    // router would treat this as "same path" and never trigger progress itself.
    setProgress(true);
    // Native router with the FINAL url on purpose: the app's custom useRouter
    // re-applies locale logic (addLocaleToHref) and would undo the default-locale
    // stripping, pushing the current URL back → no navigation.
    router.push(qs ? `${path}?${qs}` : path);
    router.refresh();
  };

  return (
    // labels: untoggled = ع (ar), toggled = EN (en)
    <BaseToggleSwitch
      mobile={mobile}
      labels={["ع", "EN"]}
      toggled={currentLocale === "en"}
      onChange={(next) => goTo(next ? "en" : "ar")}
    />
  );
};

export default LanguageSwitch;
